import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import '../config/api'; // Import to ensure axios interceptors are configured
import { useAuth } from './AuthContext';

/**
 * Feature DTO from the backend
 */
export interface FeatureDTO {
  featureCode: string;
  featureName: string;
  description: string;
  category: string;
  navPath: string | null;
  navIcon: string | null;
  navLabel: string | null;
  navGroup: string | null;
  sortOrder: number;
}

/**
 * Navigation item for sidebar
 */
export interface NavItem {
  featureCode: string;
  path: string;
  icon: string;
  label: string;
  sortOrder: number;
}

/**
 * Navigation group with items
 */
export interface NavGroup {
  groupName: string;
  groupLabel: string;
  items: NavItem[];
}

/**
 * Full tenant configuration from backend
 */
export interface TenantConfig {
  tenantId: number;
  tenantName: string;
  product: 'OMS' | 'COPILOT';
  clientType: 'INSTITUTIONAL' | 'RETAIL' | 'HYBRID';
  tier: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  primaryCustodian: string | null;
  pcFundingControlPolicy: 'REQUIRED' | 'REQUIRED_FOR_INITIAL_DRAW' | 'REQUIRED_FOR_DDTL_AND_REVOLVER' | 'WARN_ONLY' | 'DISABLED';
  enabledFeatures: FeatureDTO[];
  navigation: NavGroup[];
  featureFlags: Record<string, boolean>;
}

/**
 * Context state
 */
interface TenantConfigContextState {
  // Config data
  config: TenantConfig | null;
  isLoading: boolean;
  error: string | null;

  // Feature checks
  isFeatureEnabled: (featureCode: string) => boolean;
  hasNavPath: (path: string) => boolean;

  // Navigation
  navigation: NavGroup[];
  getNavGroupItems: (groupName: string) => NavItem[];

  // Tenant info
  product: 'OMS' | 'COPILOT';
  clientType: 'INSTITUTIONAL' | 'RETAIL' | 'HYBRID';
  tier: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE';
  tenantName: string;

  // Client type helpers
  isInstitutional: boolean;
  isRetail: boolean;
  isHybrid: boolean;

  // Tier helpers
  isStarter: boolean;
  isProfessional: boolean;
  isEnterprise: boolean;

  // Product helpers
  isOMS: boolean;
  isCopilot: boolean;

  // Common feature checks
  hasHouseholds: boolean;
  hasModelPortfolios: boolean;
  hasFeeBilling: boolean;
  hasSleeveUMA: boolean;
  hasClientPortal: boolean;
  hasTaxHarvesting: boolean;
  hasCustodianSchwab: boolean;
  hasCustodianFidelity: boolean;
  hasMiddleOffice: boolean;
  hasFixConnectivity: boolean;
  hasCustodianRecon: boolean;
  hasCorporateActions: boolean;
  hasNavCalculation: boolean;
  hasRegulatoryReporting: boolean;
  hasAIAgentsHub: boolean;
  hasAIChat: boolean;

  // Refresh
  refreshConfig: () => Promise<void>;
}

const defaultConfig: TenantConfig = {
  tenantId: 0,
  tenantName: 'Default',
  product: 'OMS',
  clientType: 'RETAIL',
  tier: 'PROFESSIONAL',
  primaryCustodian: null,
  pcFundingControlPolicy: 'REQUIRED',
  enabledFeatures: [],
  navigation: [],
  featureFlags: {}
};

const TenantConfigContext = createContext<TenantConfigContextState | undefined>(undefined);

interface TenantConfigProviderProps {
  children: ReactNode;
}

export const TenantConfigProvider: React.FC<TenantConfigProviderProps> = ({ children }) => {
  const { token, loading: authLoading } = useAuth();
  // Initialize from sessionStorage to prevent RETAIL flash on re-mount
  const [config, setConfig] = useState<TenantConfig | null>(() => {
    try {
      const cached = sessionStorage.getItem('tenantConfig');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [isLoading, setIsLoading] = useState(!config);
  // A refresh with settings already in hand happens behind the page: flagging it as loading made App
  // unmount and remount every page it gates (each page's reads ran twice, the first answers thrown away).
  const hasConfig = useRef(config != null);
  hasConfig.current = config != null;
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!token) {
      setConfig(defaultConfig);
      setError(null);
      setIsLoading(false);
      return;
    }

    try {
      if (!hasConfig.current) setIsLoading(true);
      setError(null);

      const response = await axios.get('/api/tenant-config');
      const data: TenantConfig = response.data;

      setConfig(data);
      try { sessionStorage.setItem('tenantConfig', JSON.stringify(data)); } catch {}
      console.log('Loaded tenant config:', data.tenantName, data.clientType, data.tier);

    } catch (err: any) {
      console.error('Failed to fetch tenant config:', err);
      // Only use default config if we have no existing config
      // This prevents flipping from INSTITUTIONAL to RETAIL on transient errors
      setConfig(prev => prev || defaultConfig);
      setError(err.message || 'Failed to load tenant configuration');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (authLoading) return;
    fetchConfig();
  }, [authLoading, fetchConfig]);

  // Feature check
  const isFeatureEnabled = useCallback((featureCode: string): boolean => {
    if (!config) return false;
    return config.featureFlags[featureCode] === true;
  }, [config]);

  // Check if a path exists in navigation
  const hasNavPath = useCallback((path: string): boolean => {
    if (!config) return false;
    return config.navigation.some(group =>
      group.items.some(item => item.path === path)
    );
  }, [config]);

  // Get items for a specific nav group
  const getNavGroupItems = useCallback((groupName: string): NavItem[] => {
    if (!config) return [];
    const group = config.navigation.find(g => g.groupName === groupName);
    return group?.items || [];
  }, [config]);

  // Computed values
  const product = config?.product || 'OMS';
  const clientType = config?.clientType || 'RETAIL';
  const tier = config?.tier || 'PROFESSIONAL';
  const tenantName = config?.tenantName || 'Default';
  const navigation = config?.navigation || [];

  // Client type helpers
  const isInstitutional = clientType === 'INSTITUTIONAL';
  const isRetail = clientType === 'RETAIL';
  const isHybrid = clientType === 'HYBRID';

  // Tier helpers
  const isStarter = tier === 'STARTER';
  const isProfessional = tier === 'PROFESSIONAL' || tier === 'ENTERPRISE';
  const isEnterprise = tier === 'ENTERPRISE';

  // Product helpers
  const isOMS = product === 'OMS';
  const isCopilot = product === 'COPILOT';

  // Common feature checks
  const hasHouseholds = isFeatureEnabled('HOUSEHOLDS');
  const hasModelPortfolios = isFeatureEnabled('MODEL_PORTFOLIOS');
  const hasFeeBilling = isFeatureEnabled('FEE_BILLING');
  const hasSleeveUMA = isFeatureEnabled('SLEEVE_UMA');
  const hasClientPortal = isFeatureEnabled('CLIENT_PORTAL');
  const hasTaxHarvesting = isFeatureEnabled('TAX_HARVESTING');
  const hasCustodianSchwab = isFeatureEnabled('CUSTODIAN_SCHWAB');
  const hasCustodianFidelity = isFeatureEnabled('CUSTODIAN_FIDELITY');
  const hasMiddleOffice = isFeatureEnabled('MIDDLE_OFFICE');
  const hasFixConnectivity = isFeatureEnabled('FIX_CONNECTIVITY');
  const hasCustodianRecon = isFeatureEnabled('CUSTODIAN_RECON');
  const hasCorporateActions = isFeatureEnabled('CORPORATE_ACTIONS');
  const hasNavCalculation = isFeatureEnabled('NAV_CALCULATION');
  const hasRegulatoryReporting = isFeatureEnabled('REGULATORY_REPORTING');
  const hasAIAgentsHub = isFeatureEnabled('AI_AGENTS_HUB');
  const hasAIChat = isFeatureEnabled('AI_CHAT');

  const value: TenantConfigContextState = {
    config,
    isLoading,
    error,
    isFeatureEnabled,
    hasNavPath,
    navigation,
    getNavGroupItems,
    product,
    clientType,
    tier,
    tenantName,
    isInstitutional,
    isRetail,
    isHybrid,
    isStarter,
    isProfessional,
    isEnterprise,
    isOMS,
    isCopilot,
    hasHouseholds,
    hasModelPortfolios,
    hasFeeBilling,
    hasSleeveUMA,
    hasClientPortal,
    hasTaxHarvesting,
    hasCustodianSchwab,
    hasCustodianFidelity,
    hasMiddleOffice,
    hasFixConnectivity,
    hasCustodianRecon,
    hasCorporateActions,
    hasNavCalculation,
    hasRegulatoryReporting,
    hasAIAgentsHub,
    hasAIChat,
    refreshConfig: fetchConfig,
  };

  return (
    <TenantConfigContext.Provider value={value}>
      {children}
    </TenantConfigContext.Provider>
  );
};

/**
 * Hook to access tenant configuration
 */
export const useTenantConfig = (): TenantConfigContextState => {
  const context = useContext(TenantConfigContext);
  if (context === undefined) {
    throw new Error('useTenantConfig must be used within a TenantConfigProvider');
  }
  return context;
};

/**
 * Hook to check if a feature is enabled
 */
export const useIsFeatureEnabled = (featureCode: string): boolean => {
  const { isFeatureEnabled } = useTenantConfig();
  return isFeatureEnabled(featureCode);
};

/**
 * Hook to get navigation items for a group
 */
export const useNavGroup = (groupName: string): NavItem[] => {
  const { getNavGroupItems } = useTenantConfig();
  return getNavGroupItems(groupName);
};

export default TenantConfigContext;
